import * as path from 'path'
import * as fs from 'fs'
import * as TJS from 'typescript-json-schema'
import { JSONSchemaFaker } from 'json-schema-faker'

// 为json-schema-faker提供location对象，在Node环境中处理浏览器API依赖
if (typeof global.location === 'undefined') {
  const currentUrl = new URL(import.meta.url);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (global as any).location = {
    href: currentUrl.href,
    protocol: currentUrl.protocol,
    host: currentUrl.host,
    hostname: currentUrl.hostname,
    port: currentUrl.port,
    pathname: currentUrl.pathname,
    search: currentUrl.search,
    hash: currentUrl.hash,
    origin: currentUrl.origin,
  }
}

// 配置 json-schema-faker
JSONSchemaFaker.option({
  // 总是为可选字段生成数据，即使该字段是可选的
  alwaysFakeOptionals: true,
  // 如果 schema 中定义了默认值，优先使用默认值而不是生成随机值
  useDefaultValue: true,
  // 当遇到 schema 中的引用($ref)找不到时，忽略错误继续执行
  ignoreMissingRefs: true,
  // 生成数组类型数据时的最大长度
  maxItems: 5,
  // 生成数组类型数据时的最小长度
  minItems: 1,
})

// 指定 TypeScript 编译选项
export const tjsDefaultSettings: TJS.PartialArgs = {
  required: true,
  ref: true,
  titles: true,
  validationKeywords: [
    'pattern',
    'minimum',
    'maximum',
    'format',
    'minItems',
    'maxItems',
    'minLength',
    'maxLength',
    'uniqueItems',
    'uniqueItemProperties',
    // 添加 autoIncrement 和 initialOffset 支持
    'autoIncrement',
    'initialOffset',
    // 添加 fake 和 format 支持
    'fake',
    'format',
  ],
}

/**
 * 从TypeScript文件生成JSON Schema
 * @param filePath TypeScript文件路径
 * @param symbolName 要生成Schema的类型名称
 * @param outputPath 输出路径，如果提供则保存到文件
 * @param customSettings 自定义的TypeScript JSON Schema设置
 * @returns 生成的JSON Schema
 */
export function generateSchema(
  filePath: string, 
  symbolName: string, 
  outputPath?: string,
  customSettings?: Partial<TJS.PartialArgs>,
): object {
  // 合并默认设置和自定义设置
  const settings: TJS.PartialArgs = {
    ...tjsDefaultSettings,
    ...customSettings,
    // 确保自定义的validationKeywords会被合并而不是覆盖
    validationKeywords: [
      ...(tjsDefaultSettings.validationKeywords || []),
      ...(customSettings?.validationKeywords || []),
    ],
  }

  // 创建程序和生成器
  const program = TJS.getProgramFromFiles([path.resolve(filePath)], {
    strictNullChecks: true,
  })
  const generator = TJS.buildGenerator(program, settings)

  if (!generator) {
    throw new Error('创建JSON Schema生成器失败')
  }

  // 生成 JSON Schema
  const schema = generator.getSchemaForSymbol(symbolName)

  // 如果提供了输出路径，则保存到文件
  if (outputPath) {
    const outputDir = path.dirname(outputPath)
    if (!fs.existsSync(outputDir)) {
      fs.mkdirSync(outputDir, { recursive: true })
    }
    fs.writeFileSync(outputPath, JSON.stringify(schema, null, 2))
  }

  return schema
}

/**
 * 从JSON Schema生成Mock数据
 * @param schema JSON Schema对象或Schema文件路径
 * @param outputPath 输出路径，如果提供则保存到文件
 * @returns 生成的Mock数据
 */
export async function generateMock<T = unknown>(
  schema: object | string,
  outputPath?: string,
): Promise<T> {
  let schemaObj: object

  // 如果schema是字符串，则尝试作为文件路径读取
  if (typeof schema === 'string') {
    try {
      schemaObj = JSON.parse(fs.readFileSync(schema, 'utf8'))
    } catch (error) {
      throw new Error(`读取Schema文件失败: ${error}`)
    }
  } else {
    schemaObj = schema
  }

  // 生成Mock数据
  try {
    const mockData = (await JSONSchemaFaker.resolve(schemaObj)) as T

    // 如果提供了输出路径，则保存到文件
    if (outputPath) {
      const outputDir = path.dirname(outputPath)
      if (!fs.existsSync(outputDir)) {
        fs.mkdirSync(outputDir, { recursive: true })
      }
      fs.writeFileSync(outputPath, JSON.stringify(mockData, null, 2))
    }

    return mockData
  } catch (error) {
    throw new Error(`生成Mock数据失败: ${error}`)
  }
}

/**
 * 生成多条Mock数据
 * @param schema JSON Schema对象或Schema文件路径
 * @param count 要生成的数据条数
 * @param outputPath 输出路径，如果提供则保存到文件
 * @returns 生成的Mock数据数组
 */
export async function generateMultipleMocks<T = unknown>(
  schema: object | string,
  count: number,
  outputPath?: string,
): Promise<T[]> {
  let schemaObj: object

  // 如果schema是字符串，则尝试作为文件路径读取
  if (typeof schema === 'string') {
    try {
      schemaObj = JSON.parse(fs.readFileSync(schema, 'utf8'))
    } catch (error) {
      throw new Error(`读取Schema文件失败: ${error}`)
    }
  } else {
    schemaObj = schema
  }

  // 生成多条Mock数据
  try {
    const mocks: T[] = []
    for (let i = 0; i < count; i++) {
      const mock = (await JSONSchemaFaker.resolve(schemaObj)) as T
      mocks.push(mock)
    }

    // 如果提供了输出路径，则保存到文件
    if (outputPath) {
      const outputDir = path.dirname(outputPath)
      if (!fs.existsSync(outputDir)) {
        fs.mkdirSync(outputDir, { recursive: true })
      }
      fs.writeFileSync(outputPath, JSON.stringify(mocks, null, 2))
    }

    return mocks
  } catch (error) {
    throw new Error(`生成多条Mock数据失败: ${error}`)
  }
}
